import React from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";

// One colour cue per data layer, used on every item, table and stage track.
export const LAYER = {
  Bronze: { text: "text-[#A8662A] dark:text-[#D9965A]", border: "border-[#B4712F]/55", bg: "bg-[#B4712F]/10", dot: "bg-[#B4712F]" },
  Silver: { text: "text-[#5E6D82] dark:text-[#AEBBCC]", border: "border-[#8A97A8]/60", bg: "bg-[#8A97A8]/12", dot: "bg-[#8A97A8]" },
  Gold: { text: "text-[#9A7712] dark:text-[#E0BC4A]", border: "border-[#C9A227]/60", bg: "bg-[#C9A227]/12", dot: "bg-[#C9A227]" },
};

export const STATUS = {
  draft: ["New", "info"], submitted: ["Sent for review", "info"], cleaning: ["Cleaning", "warning"],
  silver: ["Promoted to Silver", "success"], rejected: ["Rejected", "critical"], too_large: ["Too large · Phase 2", "warning"],
};
export const OPEN = ["draft", "submitted", "cleaning"];
export const SOURCE = { upload: "Upload", s3: "S3" };
export const btn = "inline-flex items-center justify-center gap-1.5 h-9 px-3.5 rounded-sm text-[12.5px] font-medium transition-[opacity,background-color,border-color,transform] active:scale-[0.98] disabled:opacity-45 disabled:cursor-not-allowed";
export const when = (iso) => (iso ? new Date(iso).toLocaleString("en-PH", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
export const num = (n) => (n ?? 0).toLocaleString("en-PH");

export function LayerChip({ layer, testid, className }) {
  const c = LAYER[layer] || LAYER.Bronze;
  return (
    <span data-testid={testid} className={cn("inline-flex items-center gap-1.5 h-5 px-1.5 rounded-sm border text-[10px] font-semibold uppercase tracking-wider", c.text, c.border, c.bg, className)}>
      <span className={cn("w-1.5 h-1.5 rounded-full", c.dot)} />{layer}
    </span>
  );
}

// Bronze → Silver → Gold with the reached stages lit in their layer colour.
export function StageTrack({ stage = "bronze", testid }) {
  const at = { bronze: 0, silver: 1, gold: 2 }[stage] ?? 0;
  return (
    <div className="inline-flex items-center gap-0.5" data-testid={testid}>
      {["Bronze", "Silver", "Gold"].map((l, i) => (
        <React.Fragment key={l}>
          {i > 0 && <ChevronRight size={10} className={i <= at ? "text-fg/60" : "text-muted/40"} />}
          <span className={cn("px-1.5 h-5 inline-flex items-center rounded-sm border text-[10px] font-semibold uppercase tracking-wider",
            i <= at ? cn(LAYER[l].text, LAYER[l].border, LAYER[l].bg) : "border-border text-muted/60")}>{l}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

const TONE = { info: "text-info border-info/50", success: "text-success border-success/50", critical: "text-critical border-critical/50", warning: "text-warning border-warning/50", neutral: "text-muted border-border" };
export const Pill = ({ children, tone = "info", testid }) => (
  <span data-testid={testid} className={cn("inline-flex items-center whitespace-nowrap h-5 px-1.5 rounded-sm border text-[10.5px] font-semibold uppercase tracking-wider", TONE[tone])}>{children}</span>
);

export const StatusPill = ({ status, testid }) => <Pill tone={(STATUS[status] || ["", "neutral"])[1]} testid={testid}>{(STATUS[status] || [status])[0]}</Pill>;

export function ScoreBars({ dims }) {
  return (
    <div className="grid grid-cols-4 gap-1.5">
      {Object.entries(dims || {}).map(([k, v]) => (
        <div key={k} title={`${k} ${v}`}>
          <div className="h-1.5 bg-surface-2 rounded-full"><div className={cn("h-full rounded-full", v >= 80 ? "bg-success" : v >= 60 ? "bg-warning" : "bg-critical")} style={{ width: `${v}%` }} /></div>
          <div className="text-[9.5px] text-muted mt-0.5 truncate capitalize">{k} {v}</div>
        </div>
      ))}
    </div>
  );
}

export const scoreTone = (s) => (s == null ? "text-muted" : s >= 80 ? "text-success" : s >= 60 ? "text-warning" : "text-critical");
